/** @format */

export const round = (number: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round(number * factor) / factor;
};

export const formatRUB = (number: number): string => {
  return Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
    // https://stackoverflow.com/questions/41045270
    minimumFractionDigits: 0,
  }).format(number);
};

export const buildFormatter = (currency: string, language: string): Intl.NumberFormat => {
  if (currency === '' || currency === 'mixed') {
    return Intl.NumberFormat(language, {
      maximumFractionDigits: 0,
      // https://stackoverflow.com/questions/41045270
      minimumFractionDigits: 0,
    });
  }
  return Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0,
    // https://stackoverflow.com/questions/41045270
    minimumFractionDigits: 0,
  });
};

interface FormattedNumber {
  value: string;
  currency: string;
}

export const buildFormatterObject = (
  currency: string,
  language: string,
): {format: (number: number) => FormattedNumber} => {
  const formatter = buildFormatter(currency, language);

  return {
    format: (number: number): FormattedNumber => {
      const formatted = formatter.format(number);

      if (currency === '' || currency === 'mixed') {
        return {
          value: formatted,
          currency: '',
        };
      }

      const parts = formatter.formatToParts(number);
      const valueParts = parts.filter(part => part.type !== 'currency');
      const currencyParts = parts.filter(part => part.type === 'currency');

      return {
        value: valueParts.map(part => part.value).join(''),
        currency: currencyParts.map(part => part.value).join(''),
      };
    },
  };
};

/**
 * Раздробит число на разряды по 3 цифры
 * Может работать с числами типа float
 * @param value
 */

export const divideBySpaces = (value: number | string): string => {
  const num = typeof value === 'number' ? value : Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  const formatter = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return formatter.format(num);
};

/**
 * Округлит число до определённого знака и удалит нули в конце числа
 * @param num
 * @param digitsAfterComma
 */

export const roundFloatNumber = (num: number, digitsAfterComma?: number): number => {
  if (digitsAfterComma === undefined) {
    return num;
  }
  const factor = Math.pow(10, digitsAfterComma);
  return Math.round(num * factor) / factor;
};

/**
 * Безопасное деление одного числа на другое
 * @param dividend
 * @param division
 */

export const safeDivide = (dividend: number, division?: number): number => {
  if (division === undefined || division === 0) {
    return 0;
  }
  return dividend / division;
};

export const safeNumber = (value: unknown): number => {
  const result = Number(value);
  return isNaN(result) ? 0 : result;
};

export const shortNumber = (value: number): string => {
  const isLimited = value >= 1000000 || value < 1000;

  const formatter = new Intl.NumberFormat('ru-RU', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: isLimited ? 1 : 0,
  });

  return formatter.format(value).replace('.', '');
};

interface ClampValueOptions {
  value: number;
  min?: number;
  max?: number;
}

export const clampValue = ({value, max, min}: ClampValueOptions): number => {
  const minValue = min !== undefined ? min : -Infinity;
  const maxValue = max !== undefined ? max : Infinity;

  if (value < minValue) {
    return minValue;
  }

  if (value > maxValue) {
    return maxValue;
  }

  return value;
};

export const getValueWithCurrency = (value: number | string, currency: string): string => {
  const isString = typeof value === 'string';
  const formatted = isString ? value : new Intl.NumberFormat('ru-RU').format(value);
  return `${formatted} ${currency}`;
};
